import { useState } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import DeletePaymentProfile from "service/paymentServices/DeletePaymentProfile";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";

const DeleteNonAutopayPaymentModal = ({
  modalState,
  setModalState,
  data,
  policies,
  isUpdated,
  setIsUpdated,
  setToastDetail,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [csrUserState, setCsrUserState] = useState(false);

  async function onSubmit() {
    if (csrUser.userId) {
      setCsrUserState(true);
      setModalState(false);
      return;
    }
    const selectedPolicies = policies?.filter(
      (p) => p?.creditCard?.recurringReferenceId !== data?.paymentProfileId
    );
    if (selectedPolicies?.length > 0) {
      const { blockCode, bankInfo } = selectedPolicies[0];
      const payload = {
        paymentProfileId: data?.paymentProfileId,
        policyId: "",
        blockCode: blockCode,
        userid: user?.userId,
        draftDay: bankInfo?.draftDay,
      };
      setToastDetail({
        type: undefined,
      });
      if (payload) {
        //delete Payment Profile
        await DeletePaymentProfile(payload)
          .then(() => {
            setIsUpdated(!isUpdated);
            setModalState(false);
            setToastDetail({
              type: "success",
              title: t("CreditCardDeleteOneTimeModal-deleteSuccessTitle"),
              message: t("CreditCardDeleteOneTimeModal-deleteSuccessDesc"),
            });
          })
          .catch((error) => {
            setIsUpdated(!isUpdated);
            console.error(
              "Server refused to connect, please try again later.",
              error
            );
            setToastDetail({
              type: "error",
              title: t("CreditCardDeleteOneTimeModal-deleteFailureTitle"),
              message: t("CreditCardDeleteOneTimeModal-deleteFailureDesc"),
            });
            setModalState(false);
          });
      } else {
        setIsUpdated(!isUpdated);
        setModalState(false);
        setToastDetail({
          type: "error",
          title: t("CreditCardDeleteOneTimeModal-deleteFailureTitle"),
          message: t("CreditCardDeleteOneTimeModal-deleteFailureDesc"),
        });
      }
    }
  }
  return (
    <>
      <Dialog
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 top-[20%] z-20 overflow-y-auto"
        open={modalState}
        onClose={() => setModalState(false)}
      >
        <DialogBackdrop />
        <Dialog.Panel className="ui-default-dialog md:max-w-xl">
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("CreditCardDeleteOneTimeModal-title")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            onClick={() => setModalState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <hr />

          <Dialog.Description as="div">
            <p>{t("CreditCardDeleteOneTimeModal-desc")}</p>
            <div className="p-2 mt-4 border border-gray-light bg-ui-gray-lightest rounded-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-none lg:grid-flow-col gap-4">
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CreditCardDeleteOneTimeModal-label-1")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.accountNumber}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CreditCardDeleteOneTimeModal-label-2")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.ownerName}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CreditCardDeleteOneTimeModal-label-3")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.expDate}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CreditCardDeleteOneTimeModal-label-4")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.cardType}
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Description>
          <div className="block text-right mt-6">
            <button
              className="ui-btn-secondary ui-btn-min-w mt-4"
              onClick={() => setModalState(false)}
            >
              {t("CreditCardDeleteOneTimeModal-buttonCancel")}
              <span className="sr-only">
                {" "}
                {t("CreditCardDeleteOneTimeModal-buttonSrText")}
              </span>
            </button>
            <button
              className="ui-btn-primary ui-btn-min-w mt-4"
              onClick={() => onSubmit()}
            >
              {t("CreditCardDeleteOneTimeModal-buttonYes")}
              <span className="sr-only">
                {" "}
                {t("CreditCardDeleteOneTimeModal-buttonSrText")}
              </span>
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
      <GeneralPurposeDialog
        title={t("CSRErrorMessage-title")}
        desc={t("CSRErrorMessage-message")}
        state={csrUserState}
        setState={setCsrUserState}
        showCloseButton={false}
      />
    </>
  );
};

export default DeleteNonAutopayPaymentModal;
